import React, { useState, useCallback, useEffect, useRef } from 'react'
import { Input, Tooltip, Typography, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import ErrorIcon from '@material-ui/icons/ErrorOutline'
import classnames from 'classnames'
import debounce from 'lodash/debounce'
import { BUILDER_V2_INK, BUILDER_V2_BORDER } from '../../util'

interface StringOptionProps {
  classes?: StyleClasses<typeof useStyles>
  errorMessage?: string
  label: string
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
  value: string
}

const useStyles = makeStyles<Theme, StringOptionProps>(
  (theme: Theme) => {
    return {
      root: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        minHeight: 44
      },
      label: {
        fontSize: '.8125rem',
        fontWeight: 500,
        flexShrink: 0,
        marginRight: theme.spacing(3),
        color: BUILDER_V2_INK
      },
      inputRoot: {
        flex: '1 1 auto',
        minWidth: 0,
        maxWidth: 180,
        borderColor: BUILDER_V2_BORDER,
        borderRadius: 5
      },
      input: {
        padding: '4px 8px',
        fontSize: '.8125rem',
        color: BUILDER_V2_INK
      },
      invalidInput: {},
      tooltipRoot: {
        marginLeft: theme.spacing(2),
        marginRight: 'auto'
      },
      tooltipBadge: {
        cursor: 'pointer',
        top: 0,
        right: 0,
        '& svg': {
          fontSize: '1rem'
        }
      }
    }
  },
  { name: 'StringOption' }
)

export default function StringOption(props: StringOptionProps) {
  const classes = useStyles(props)
  const { errorMessage, label, onChange, value: propsValue } = props
  const [value, setValue] = useState(propsValue)

  // the debounced fn below is only created once (its 400ms timer must stay stable across
  // keystrokes) but `onChange` closes over the parent's current field/file state, which changes
  // every render — routing through a ref keeps the debounce alive while always calling the
  // latest `onChange`, instead of a `useCallback`-frozen one that silently overwrote other
  // in-flight image-option edits with its stale snapshot
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const handleOnChange = useCallback(
    debounce((ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChangeRef.current(ev)
    }, 400),
    // intentionally empty — the debounce timer must stay the same function across renders;
    // onChangeRef (not onChange) is what keeps this current, see the comment above
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const batchOnChange = (ev: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValue(ev.target.value)
    handleOnChange(ev)
  }

  return (
    <div className={classes.root}>
      <Typography className={classes.label} variant={'h5'}>
        {label}
      </Typography>
      {errorMessage && (
        <div className={classes.tooltipRoot}>
          <Tooltip
            placement={'left'}
            content={errorMessage}
            icon={<ErrorIcon />}
            classes={{ badge: classes.tooltipBadge }}
          />
        </div>
      )}
      <Input
        classes={{
          root: classnames(classes.inputRoot, { [classes.invalidInput]: errorMessage }),
          input: classes.input
        }}
        value={value}
        onChange={(ev) => batchOnChange(ev)}
      />
    </div>
  )
}
